import { Injectable } from '@angular/core';
import { AppInjector } from '../../app-injector';
import { IntegrationItemServiceInf, StatusString, UserLicense } from '../interfaces/integrations';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from 'src/app/core/services/admin.service';
import { Account, User } from '@mitel/cloudlink-sdk';
import { AccountsService } from './accounts.service';
import { SpinnerService } from './spinner.service';
import { OrdersService } from './orders.service';
import { mivoice5000Licenses } from '../constants/constants';
import { UsersBulkEditMitel5000Service } from './users-bulk-edit-mitel-5000.service';
import { UserMitel5000SharedService } from './user-mitel-5000-shared.service';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';

@Injectable({
	providedIn: 'root'
})

export class IntegMitel5000Service implements IntegrationItemServiceInf {

	private adminService: AdminService;
	private accountsService: AccountsService;
	private spinnerService: SpinnerService;
	public dialog: MatDialog;
	private orderService: OrdersService;
	private userBulkEditService: UsersBulkEditMitel5000Service;
	private mitel5000SharedSvc: UserMitel5000SharedService;
	private rbacAuthGuardService: RBACAuthGuardService;

	constructor(  ) 
	{
	}

	initServices()
	{
		if ( !this.adminService )
			this.adminService = AppInjector.injector.get(AdminService);

		if ( !this.accountsService )
			this.accountsService = AppInjector.injector.get(AccountsService);

		if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		if ( !this.orderService )
			this.orderService = AppInjector.injector.get(OrdersService);

		if ( !this.dialog )
			this.dialog = AppInjector.injector.get(MatDialog);

		if ( !this.userBulkEditService )
			this.userBulkEditService = AppInjector.injector.get(UsersBulkEditMitel5000Service);

		if ( !this.mitel5000SharedSvc )
			this.mitel5000SharedSvc = AppInjector.injector.get(UserMitel5000SharedService);

		if (!this.rbacAuthGuardService)
			this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

	}

	canBeSelected(account: Account): Promise<boolean>{
		this.initServices();

		let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONMIVOICE5000, ActionType.VIEW);
		return Promise.resolve(!isDenied);
	}

	getConfigurationDialog() : any
	{
		return undefined;
	}

	isSetupComplete(account: Account) : Promise<boolean>
	{
		return Promise.resolve(true);
	}

	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any>
	{
		this.initServices();

		if ( state == false )
		{
			this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
			this.spinnerService.showSpinner();
			return this.adminService.getUsersByAccountIdWithoutSpinner(account.accountId, {$Expand: 'tags'}).then(users=>{

				//checks if there is miVoice 5000 subscription, if yes then licenses are assigned to users or not
				return this.mitel5000SharedSvc.getRemainingLicenseDetails().then( licenses => {
					this.spinnerService.hideSpinner();
					if ( Object.keys(licenses).length > 0 )
					{
							let assignedUserLicenses : boolean;
							Object.keys(licenses).forEach( license => {
							if(licenses[license].used != 0){
								assignedUserLicenses = true;
							}
						});

						//if there are users with the licenses assigned, then remove the licenses
						if ( assignedUserLicenses )
						{
							// now remove all the licenses from all users
							return this.userBulkEditService.saveUserMitel5000BulkOperation(
								account.accountId, users, {license: "unassignAll"}).then(result => {
								this.orderService.remainingLicensesChanged.next(); // need the license display to update
								return Promise.resolve();
							});
						}
					}
					return Promise.resolve();
				})
			})
		}
		else
		{
			return Promise.resolve();
		}

	}

	getStatusString(account: Account): Promise<StatusString>
	{
		return undefined;
	}

	getConfigButtonLabel(account: Account): Promise<string>
	{
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean>
	{
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean>
	{
		this.initServices();
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.REMOVE_MITEL5000_CONFIRMATION } });
		return dialogRef.afterClosed().toPromise().then(result => {
			return result;
		});
	}

	canFilterUsersOnProduct(account: Account): boolean
	{
		return true;
	}

	async filterUsersBasedOnProduct(account: Account, users: User[], filter: any[] ): Promise<User[]>
	{
		this.initServices();
		let newUsersList = new Array<User>();
	
		for ( var i = 0; i < users.length; i++ )
		{
			try
			{
				let licenses = await this.orderService.getUsersLicensesFromCachedList(users[i].userId);
				for ( var j = 0; j < licenses.length; j++ )
				{
					if(mivoice5000Licenses.includes(licenses[j]))
					{
						newUsersList.push(users[i]);
					}  
				}

			}
			catch(e)
			{
				console.log("Is there an issue here?", e)
			}
		}
		return newUsersList;
	}

	getUserLicense( user: User, account: Account ): Promise<UserLicense>
	{
		this.initServices();

		return this.orderService.getUsersLicensesFromCachedList(user.userId).then( licenses => {
			let userLicense: UserLicense;
			for ( var i = 0; i < licenses.length; i++ )
			{
				if(mivoice5000Licenses.includes(licenses[i])){
					userLicense = {
						code: licenses[i],
						displayStringKey: 'product_licenses.mivoice5000.' + licenses[i]
					}
					return userLicense;
				}  
			}
			return undefined;
		});
	}

}
